import React, { useMemo, useState } from "react";
import { Spin, Modal, Alert } from "antd";
import { FullscreenOutlined, FilePdfOutlined } from "@ant-design/icons";
import ReactFlow, { Background, Controls, ReactFlowProvider, MarkerType } from "react-flow-renderer";
import "react-flow-renderer/dist/style.css";
import { ButtonItem } from "./Items";
import TarjetaParadaPicking from "./TarjetaParadaPicking";
import { colorDeSegmento, COLOR_ENCIMADO, COLOR_ACTUAL } from "./rutaPickingColores";
import NodoCanvas from "../Pages/Panel/Ubicaciones/MapaAlmacenEditor/Components/NodoCanvas";
import SeccionCanvas from "../Pages/Panel/Ubicaciones/MapaAlmacenEditor/Components/SeccionCanvas";
import RackCanvas from "../Pages/Panel/Ubicaciones/MapaAlmacenEditor/Components/RackCanvas";

const nodeTypes = { punto: NodoCanvas, seccion: SeccionCanvas, rack: RackCanvas };
const PREFIJO_NODO = "nodo-";
const PREFIJO_SECCION = "seccion-";
const PREFIJO_RACK = "rack-";
const NODO_MITAD_TAMANO = 17;

//Arma los nodes/edges de React Flow para dibujar el almacén + la ruta resaltada - se usa igual tanto para
//el mini-mapa compacto como para la vista de pantalla completa (mismos datos, distinto tamaño de lienzo).
const useNodosYEdges = ({ datosMapa, segmentosCamino, paradas, indiceParadaActual }) => {
  const numeroSegmentoActual = indiceParadaActual != null ? indiceParadaActual + 1 : null;

  const estadoPorNodo = useMemo(() => {
    const mapa = new Map();
    (paradas || []).forEach((valorParada, indice) => {
      if (!valorParada.nodoAccesoId) return;
      const estado =
        indiceParadaActual == null ? "pendiente" : indice < indiceParadaActual ? "completada" : indice === indiceParadaActual ? "actual" : "pendiente";
      mapa.set(String(valorParada.nodoAccesoId), { numeroParada: valorParada.orden, estado });
    });
    return mapa;
  }, [paradas, indiceParadaActual]);

  const segmentosPorConexion = useMemo(() => {
    const mapa = new Map();
    (segmentosCamino || []).forEach((valorSegmento) => {
      (valorSegmento.conexionIds || []).forEach((conexionId) => {
        const llave = String(conexionId);
        if (!mapa.has(llave)) mapa.set(llave, []);
        mapa.get(llave).push(valorSegmento.numeroSegmento);
      });
    });
    return mapa;
  }, [segmentosCamino]);

  // Rack(s) a los que hay que ir para la parada actual (un nodo de acceso puede servir racks a ambos
  // lados) - se resaltan en el mapa igual que en el PDF (ver rutaPickingPdf.js) para que sea obvio "a
  // dónde ir" además del segmento de camino resaltado.
  const rackIdsDestacados = useMemo(() => {
    if (indiceParadaActual == null) return new Set();
    return new Set((paradas?.[indiceParadaActual]?.rackIds || []).map(String));
  }, [paradas, indiceParadaActual]);

  return useMemo(() => {
    if (!datosMapa) return { nodes: [], edges: [] };
    const { secciones = [], nodos: nodosCrudos = [], conexiones: conexionesCrudas = [], racks: racksCrudos = [] } = datosMapa;

    const nodosFlow = [
      ...secciones.map((valorSeccion) => ({
        id: PREFIJO_SECCION + valorSeccion._id,
        type: "seccion",
        position: { x: valorSeccion.limitesVisuales?.x || 0, y: valorSeccion.limitesVisuales?.y || 0 },
        style: { width: valorSeccion.limitesVisuales?.ancho || 240, height: valorSeccion.limitesVisuales?.alto || 200 },
        zIndex: -1,
        draggable: false,
        selectable: false,
        data: { ...valorSeccion, anchoActual: valorSeccion.limitesVisuales?.ancho || 240, altoActual: valorSeccion.limitesVisuales?.alto || 200 },
      })),
      ...racksCrudos.map((valorRack) => ({
        id: PREFIJO_RACK + valorRack._id,
        type: "rack",
        position: { x: valorRack.x, y: valorRack.y },
        style: { width: valorRack.ancho, height: valorRack.alto },
        zIndex: rackIdsDestacados.has(String(valorRack._id)) ? 5 : 2,
        draggable: false,
        selectable: false,
        data: { ...valorRack, anchoActual: valorRack.ancho, altoActual: valorRack.alto, esRackDestino: rackIdsDestacados.has(String(valorRack._id)) },
      })),
      ...nodosCrudos.map((valorNodo) => {
        const estadoNodo = estadoPorNodo.get(String(valorNodo._id));
        return {
          id: PREFIJO_NODO + valorNodo._id,
          type: "punto",
          position: { x: valorNodo.x - NODO_MITAD_TAMANO, y: valorNodo.y - NODO_MITAD_TAMANO },
          zIndex: estadoNodo?.estado === "actual" ? 4 : 3,
          draggable: false,
          selectable: false,
          data: { ...valorNodo, numeroParada: estadoNodo?.numeroParada ?? null, estadoParada: estadoNodo?.estado },
        };
      }),
    ];

    const edgesFlow = conexionesCrudas.map((valorConexion) => {
      const segmentosQueLaUsan = segmentosPorConexion.get(String(valorConexion._id)) || [];
      const encimada = segmentosQueLaUsan.length > 1;
      const enRuta = segmentosQueLaUsan.length > 0;
      const esActual = numeroSegmentoActual != null && segmentosQueLaUsan.includes(numeroSegmentoActual);
      const yaCaminado = numeroSegmentoActual != null && enRuta && segmentosQueLaUsan.every((n) => n < numeroSegmentoActual);

      let colorRuta = null;
      let strokeWidth = 0.5;
      let dash;
      let opacity = 1;

      if (enRuta) {
        if (esActual) {
          colorRuta = COLOR_ACTUAL;
          strokeWidth = 5;
          dash = "2 5";
        } else if (encimada) {
          colorRuta = COLOR_ENCIMADO;
          strokeWidth = 4;
          dash = "6 3";
        } else {
          colorRuta = colorDeSegmento(segmentosQueLaUsan[0]);
          strokeWidth = 3;
        }
        if (yaCaminado && !esActual) opacity = 0.3;
      } else {
        colorRuta = valorConexion.bloqueada ? "#ef4444" : "#d9dde5";
        dash = valorConexion.bloqueada ? "4 2" : undefined;
      }

      return {
        id: "conexion-" + valorConexion._id,
        source: PREFIJO_NODO + valorConexion.nodoOrigenId,
        target: PREFIJO_NODO + valorConexion.nodoDestinoId,
        markerEnd: enRuta ? { type: MarkerType.ArrowClosed, color: colorRuta } : undefined,
        style: { stroke: colorRuta, strokeWidth, strokeDasharray: dash, opacity },
        zIndex: esActual ? 11 : enRuta ? 10 : 0,
      };
    });

    return { nodes: nodosFlow, edges: edgesFlow };
  }, [datosMapa, estadoPorNodo, segmentosPorConexion, numeroSegmentoActual, rackIdsDestacados]);
};

// padding amplio + minZoom bajo: se ve "más de lejos" por default y se puede alejar más con scroll/pinch
// (react-flow-renderer trae minZoom=0.5 por default, que no dejaba alejarse lo suficiente en almacenes grandes).
const LienzoRuta = ({ nodes, edges, altura }) => (
  <div style={{ height: altura, border: "1px solid #eef0f6", borderRadius: 8 }}>
    <ReactFlowProvider>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        panOnScroll
        zoomOnScroll
        minZoom={0.1}
        fitView
        fitViewOptions={{ padding: 0.4 }}
      >
        <Background />
        <Controls showInteractive={false} />
      </ReactFlow>
    </ReactFlowProvider>
  </div>
);

const Leyenda = ({ segmentosCamino, indiceParadaActual }) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 8, fontSize: 11, color: "#6b7280" }}>
    {indiceParadaActual != null && (
      <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: COLOR_ACTUAL }} />
        Sigue por aquí
      </span>
    )}
    {segmentosCamino.map((valorSegmento) => (
      <span key={valorSegmento.numeroSegmento} style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: colorDeSegmento(valorSegmento.numeroSegmento) }} />
        Tramo {valorSegmento.numeroSegmento}
      </span>
    ))}
    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
      <span style={{ width: 10, height: 10, borderRadius: "50%", background: COLOR_ENCIMADO }} />
      Encimado (se camina más de una vez)
    </span>
  </div>
);

//Mini-mapa de SOLO LECTURA que dibuja el almacén (secciones, racks, nodos) y resalta encima la ruta de
//picking calculada por el grafo: cada tramo (parada a parada) se pinta de un color distinto, y en rojo
//cuando dos tramos se encima sobre la misma conexión. Si se pasa `indiceParadaActual` (el índice de la
//próxima parada sin marcar en el checklist), el tramo hacia ahí se resalta fuerte en naranja y los ya
//recorridos se atenúan - así el mapa va señalando "sigue por aquí" a medida que marcas paradas.
//El botón "Ampliar" abre el mismo dibujo más grande, y AHÍ es donde de verdad se va siguiendo la ruta: se
//muestra la tarjeta de la parada actual (con sus checkboxes) al lado del mapa, para poder ir marcando sin
//salir de la vista ampliada - el mapa se actualiza solo al marcar (avanza el resaltado a la siguiente parada).
//También tiene botón de imprimir ahí (delega en `onImprimir`, que ya arma el PDF con el mismo diagrama).
//No es editable (sin drag, sin conectar, sin abrir paneles). Si el almacén no tiene mapa, no renderiza nada.
const VisorRutaPickingMapa = ({
  mapaAlmacenId,
  datosMapa,
  cargando,
  segmentosCamino,
  paradas,
  indiceParadaActual,
  recolectado,
  marcar,
  cambiarCantidad,
  onImprimir,
  titulo,
  accionesInsumo,
}) => {
  const [pantallaCompleta, setPantallaCompleta] = useState(false);
  const { nodes, edges } = useNodosYEdges({ datosMapa, segmentosCamino, paradas, indiceParadaActual });

  if (!mapaAlmacenId) return null;

  const paradaActual = indiceParadaActual != null ? paradas[indiceParadaActual] : null;
  const segmentoActual = indiceParadaActual != null ? segmentosCamino[indiceParadaActual] : null;

  return (
    <div style={{ marginBottom: 16 }}>
      <Spin spinning={cargando}>
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}>
          <ButtonItem size="small" lila icon={<FullscreenOutlined />} onClick={() => setPantallaCompleta(true)}>
            Ampliar
          </ButtonItem>
        </div>
        <LienzoRuta nodes={nodes} edges={edges} altura={340} />
        {(segmentosCamino || []).length > 0 && <Leyenda segmentosCamino={segmentosCamino} indiceParadaActual={indiceParadaActual} />}
      </Spin>

      <Modal
        title={titulo || "Ruta de picking - mapa"}
        visible={pantallaCompleta}
        onCancel={() => setPantallaCompleta(false)}
        width="92vw"
        style={{ top: 20 }}
        bodyStyle={{ height: "84vh", padding: 12, display: "flex", flexDirection: "column" }}
        footer={
          onImprimir && (
            <ButtonItem icon={<FilePdfOutlined />} onClick={onImprimir}>
              Imprimir
            </ButtonItem>
          )
        }
      >
        <LienzoRuta nodes={nodes} edges={edges} altura="56vh" />
        {(segmentosCamino || []).length > 0 && <Leyenda segmentosCamino={segmentosCamino} indiceParadaActual={indiceParadaActual} />}
        <div style={{ marginTop: 12, overflowY: "auto", flex: 1 }}>
          {paradaActual ? (
            <TarjetaParadaPicking
              parada={paradaActual}
              segmento={segmentoActual}
              esActual
              recolectado={recolectado}
              marcar={marcar}
              cambiarCantidad={cambiarCantidad}
              accionesInsumo={accionesInsumo}
            />
          ) : (
            <Alert type="success" showIcon message="¡Ruta completada! Ya se marcaron todas las paradas." />
          )}
        </div>
      </Modal>
    </div>
  );
};

export default VisorRutaPickingMapa;
